<template>
  <div class="description-form-new">
    <div class="row" v-if="loaded && !editBlock">
      <div class="col-lg-6 visit-modal-col-divider">
        <div class="pb-lg-0">
          <div class="row row-narrower align-items-end">
            <div class="col-md-6 col-12">
              <ServiceMultiSelect :label="$t('treating_doctor')"
                                  :dblclick="false"
                                  model="doctor"
                                  formLabelClass="gray-label"
                                  :readonly="!canBeEdited"
                                  :editMode="canBeEdited"
                                  :required="true"
                                  :multiple="false"
                                  :prepopulated-options="doctors"
                                  :internalSearch="true"
                                  title="full_name"
                                  :dbsearch="false"
                                  v-model="doctor"/>
              <ValidationError validationId="doctor_id"/>
            </div>
            <div class="col-md-6 col-12"
                 v-if="chairs && chairs.length">
              <ServiceMultiSelect :label="isDentalClinic ? $t('chair') : $t('cabinet')"
                                  :dblclick="false"
                                  model="chair"
                                  formLabelClass="gray-label"
                                  :readonly="!canBeEdited"
                                  :editMode="canBeEdited"
                                  :required="chairs.length > 0"
                                  :multiple="false"
                                  :prepopulated-options="chairs"
                                  :internalSearch="true"
                                  title="chair_name"
                                  :dbsearch="false"
                                  v-model="chair"/>
              <ValidationError validationId="chair_id"/>
            </div>
          </div>
          <div class="row row-narrower align-items-end">
            <div class="col-md-6 col-6">
              <SelectGroup :options="recordTypes"
                           :showlabel="true"
                           formLabelClass="gray-label"
                           :required="true"
                           :nullOption="false"
                           :readonly="hasControlSystemStatus || isOnlineBooking"
                           :editMode="canBeEdited && !hasControlSystemStatus && !isOnlineBooking"
                           :label="$t('record_type')"
                           variant="white"
                           v-model="visit.record_type_id"/>
            </div>
            <div class="col-md-6 col-6">
              <div class="form-group">
                <label class="form-label gray-label">{{ $t('duration') }}, {{ $t('min_short') }}</label>
                <div class="d-flex align-items-center visit-duration-control"
                     v-if="canBeEdited"
                     :class="{ disabled: !canBeEdited }">
                  <a class="pointer visit-duration-control-btn"
                     @click="durationChange('minus')">-</a>
                  <input type="number"
                         class="visit-duration-control-input text-center"
                         :readonly="!canBeEdited"
                         v-model="duration"/>
                  <a class="pointer visit-duration-control-btn"
                     @click="durationChange('plus')">+</a>
                </div>
                <div v-else>{{ duration }}</div>
              </div>
            </div>
          </div>
          <ValidationError validationId="end"/>

          <div v-if="canBeEdited">
            <TimeGroup :required="true"
                       :key="`time-${timecounter}`"
                       class="w-100"
                       formLabelClass="gray-label"
                       :allow-manual="true"
                       :readonly="!canBeEdited"
                       :label="$t('visit_time')"
                       v-model="time"/>
            <ValidationError validationId="start"/>
          </div>

          <div v-if="conflictingRecord"
               class="text-danger small">
            {{ $t('record_conflict') }}:
            <span v-if="conflictingRecord.patient">{{ conflictingRecord.patient.full_name }}| </span>
            {{ conflictingRecord.readableStart }}
            <span v-if="conflictingRecordDoctor">| {{ conflictingRecordDoctor.full_name }}</span>
          </div>

          <div :class="{ 'mt-3': canBeEdited }">
            <VisitDateStrip v-model="date"
                            :visit="visit"
                            :readonly="!canBeEdited"
                            @input="dateChanged"/>
          </div>

          <div class="mt-3"
               v-if="visit.doctor_id && canBeEdited">
            <VisitTimeSlotGrid v-model="time"
                               :slots="doctorAvailableTimes || []"
                               :duration="parseInt(duration)"
                               :readonly="!canBeEdited"
                               @select="timeSlotSelected"/>
            <div class="small text-danger mt-2"
                 v-if="!doctorAvailableTimes || doctorAvailableTimes.length === 0">
              {{ $t('doctor_has_no_available_time_at_this_day') }}:
              <router-link :to="{ name: 'StaffDoctorSchedule', params: { id: visit.doctor_id }, query: { modal: 'schedule'} }">{{ $t('edit_schedule') }}</router-link>
            </div>
          </div>

          <div class="mt-3"
               v-if="isOnlineBooking && visit.online_booking_record">
            <div class="card bg-light p-3">
              <div v-if="visit.possiblePatients && visit.possiblePatients.length">
                {{ $t('by_phone_found', { phone: visit.online_booking_record.phone}) }}:
                <a class="text-underlined d-block pointer"
                   v-for="p in visit.possiblePatients"
                   @click="patient = p"
                   :key="`pat-p-${p.id}`">{{ p.full_name }} ({{ p.phone }})
                </a>
              </div>
              <div v-else>{{ $t('by_phone_nobody_found', { phone: visit.online_booking_record.phone}) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="row row-narrower align-items-start">
          <div class="col-12">
            <div class="row row-narrow align-items-start">
              <div class="col flex-grow-1">
                <ServiceMultiSelect :label="$t('patient')"
                                    :dblclick="false"
                                    model="patient"
                                    formLabelClass="gray-label"
                                    :custom-options-label="(e) => `
                <div class='row align-items-center no-gutters'>
                    <div class='col'>
                        <div>${e.full_name}</div>
                        ${!rights.includes('hide_patient_phone') ?
              `<div style='opacity: 0.55; line-height: normal; font-size: 85%; padding-bottom: 7px;'>${e.phone}</div>` : ''}
                    </div>
                    <div class='col-auto'>
                        <div style='max-width: 130px; opacity: 0.55; word-break: break-all; overflow-wrap: break-word; text-align: right; line-height: normal'>${(e.tags || []).map(x => x.title).join('<br/>')}</div>
                    </div>
                </div>`"
                                    :readonly="!patientCanBeEdited"
                                    :editMode="canBeEdited"
                                    :required="visit.record_type !== 'block'"
                                    :multiple="false"
                                    :internalSearch="false"
                                    title="full_name"
                                    ref="patientSelect"
                                    :key="`patient-select-key-${patientSelectKey}`"
                                    :dbsearch="true"
                                    :allowLoadOnScroll="true"
                                    v-model="patient">
                  <PatientNotesBlock v-if="patient"
                                     :notes="patientNotes"
                                     class="small mt-2"/>
                </ServiceMultiSelect>
                <ValidationError validationId="client_id"/>
              </div>
              <div class="col-auto">
                <div class="form-group">
                  <div class="form-label" style="margin-top: 2px;">&nbsp;</div>
                  <a v-if="canBeEdited && patientCanBeEdited"
                     @click="addPatient"
                     class="btn btn-themed gray form-control-btn-square">
                    <PlusSvg class="svg-gray"/>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <SelectGroup :options="visitPayTypes"
                         :nullOption="false"
                         :showlabel="true"
                         formLabelClass="gray-label"
                         :required="true"
                         :readonly="!polisCanBeEdited"
                         :editMode="canBeEdited"
                         :label="$t('visit_type')"
                         variant="white"
                         v-model="visitPayType"/>
          </div>
          <div class="col-md-6">
            <SelectGroup :options="statuses"
                         :nullOption="false"
                         :showlabel="true"
                         formLabelClass="gray-label"
                         :required="true"
                         :readonly="hasControlSystemStatus"
                         :editMode="canBeEdited"
                         :label="$t('status')"
                         v-if="rights.includes('recording_status')"
                         variant="white"
                         v-model="visit.records_statuses_id"/>
            <ValidationError validationId="records_statuses_id"/>
          </div>
          <template v-if="visitPayType === 'insurance'">
            <div class="col-md-6">
              <ServiceMultiSelect :label="$t('insurance_company')"
                                  :dblclick="false"
                                  model="insurance_company"
                                  title="title"
                                  formLabelClass="gray-label"
                                  :readonly="!polisCanBeEdited"
                                  :editMode="canBeEdited"
                                  :only-id="true"
                                  :multiple="false"
                                  :dbsearch="true"
                                  :prepopulatedOptions="visit.insurance_company ? [visit.insurance_company] : []"
                                  v-model="visit.insurance_company_id"/>
              <ValidationError validationId="insurance_company_id"/>
            </div>
            <div class="col-md-6">
              <label class="form-label gray-label">{{ $t('insurance_polis') }}</label>
              <div class="row row-narrow">
                <div class="col">
                  <SelectGroup :options="polisesOptions"
                               title="title"
                               variant="white"
                               :showErrors="true"
                               :readonly="!canBeEdited"
                               :editMode="canBeEdited"
                               validationId="insurance_polis_id"
                               :label="$t('insurance_polis')"
                               :showlabel="false"
                               v-model="visit.insurance_polis_id"/>
                </div>
                <div class="col-auto">
                  <a @click="addPolis"
                     class="btn btn-themed gray form-control-btn-square">
                    <PlusSvg class="svg-gray"/>
                  </a>
                </div>
              </div>
            </div>
          </template>
          <div class="col-md-6">
            <SelectGroup :options="clientSources"
                         :nullOption="true"
                         :showlabel="true"
                         formLabelClass="gray-label"
                         :translate="false"
                         :required="false"
                         :editMode="canBeEdited"
                         :readonly="hasControlSystemStatus"
                         :label="$t('source')"
                         variant="white"
                         v-model="visit.source_id"/>
            <ValidationError validationId="source_id"/>
          </div>
          <div class="col-md-6">
            <ServiceMultiSelect :label="$t('fields')"
                                :dblclick="false"
                                :multiple="false"
                                formLabelClass="gray-label"
                                :readonly="hasControlSystemStatus"
                                :editMode="canBeEdited"
                                title="translatedTitle"
                                model="field"
                                :dbsearch="true"
                                v-model="visit.fields"/>
          </div>
          <div class="col-12">
            <div class="parent-gray-label">
              <AutosizeTextArea v-model="visit.description"
                                labelclass="gray-label"
                                :minH="isMobile ? 32 : 40"
                                :rows="1"
                                :label="$t('visit_comment')"/>
            </div>
          </div>
          <div class="col-12"
               v-if="recordTags.length">
            <label class="form-label gray-label">{{ $t('tags') }}</label>
            <div class="d-flex align-items-center flex-wrap gap-2" :class="{ 'mb-2': visit.tags.length }">
              <span v-for="(t, tind) in visit.tags"
                    :key="`made-tag-${tind}`"
                    class="visit-tag-chip">
                <span><TagSvg :style="{ fill: t.color }"/></span>
                <span>{{ t.title }}</span>
                <a class="pointer font-size-0"
                   v-if="canBeEdited"
                   @click="toggleTag(t, 'remove', tind)">
                    <CloseSvg class="svg-gray svg-size-sm"/>
                </a>
                </span>
            </div>
            <div v-if="!visit.tags.length && !canBeEdited">-</div>
          </div>
          <div class="col-12" v-if="canBeEdited">
            <b-dropdown right
                        variant="link"
                        no-caret
                        v-if="currentRecordTags.length < recordTags.length">
              <template #button-content>
                <a class="pointer btn btn-themed gray form-control-btn-square">
                  <PlusSvg class="svg-gray"/>
                </a>
              </template>
              <div class="pt-0">
                <template v-for="(t, tind) in recordTags">
                  <a class="dropdown-item pointer d-flex gap-2 align-items-center"
                     :key="`tag-${tind}`"
                     @click="toggleTag(t, 'add')"
                     v-if="!currentRecordTags.includes(t.id)">
                    <span><TagSvg :style="{ fill: t.color }"/></span>
                    <span>{{ t.title }}</span>
                  </a>
                </template>
              </div>
            </b-dropdown>
          </div>
          <div class="col-12"
               v-if="maxEnabled">
            <CheckboxGroup v-model="max_messenger_used"
                           class="mt-3"
                           :readonly="!canBeEdited"
                           fid="max_messenger_used"
                           :label="$t('agreement_to_get_notifications_in_max')"/>
          </div>
        </div>
      </div>
    </div>

    <div v-if="editBlock">
      <AutosizeTextArea v-model="visit.description"
                        labelclass="gray-label"
                        :label="$t('visit_comment')"/>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue"
import {useStore} from "vuex"
import ValidationError from "@/components/form/ValidationError"
import SelectGroup from "@/components/form/SelectGroup"
import TimeGroup from "@/components/form/TimeGroup"
import ServiceMultiSelect from "@/components/form/ServiceMultiSelect"
import { DoctorService } from "@/services/api/doctor.api.service"
import { EloquentService } from "@/services/api/eloquent.api.service"
import moment from "moment"
import { recordStatuses, visitPayTypes } from "@/dictionaries/dictionary"
import AutosizeTextArea from "@/components/form/AutosizeTextArea"
import PlusSvg from "@/assets/svg-vue/services/plus_service.svg"
import TagSvg from "@/assets/svg-vue/patients/tag.svg"
import PatientNotesBlock from "@/components/parts/patients/PatientNotesBlock.vue"
import CheckboxGroup from "@/components/form/CheckboxGroup.vue"
import VisitDateStrip from "@/components/parts/calendar/visitmodal/VisitDateStrip.vue"
import VisitTimeSlotGrid from "@/components/parts/calendar/visitmodal/VisitTimeSlotGrid.vue"
import CloseSvg from "@/assets/svg-vue/general/close.svg.vue"

const props = defineProps({
  visit: {},
  hasControlSystemStatus: {}
})

const emit = defineEmits([
  'changedPatient',
  'dateIsInSchedule',
  'timeChanged'
])

const store = useStore()

const date = ref(null)
const time = ref(null)
const duration = ref(60)
const patient = ref(null)
const chair = ref(null)
const doctor = ref(null)
const doctorAvailableTimes = ref(null)
const statuses = recordStatuses
const loaded = ref(false)
const timecounter = ref(1)
const patientSelectKey = ref(1)
const visitPayType = ref('payable')
const conflictingRecord = ref(null)
const patientNotes = ref([])
const max_messenger_used = ref(false)
const dateWatcher = ref(false)
const patientSelect = ref(null)

const doctorsFromAuth = computed(() => store.state.auth.doctors)
const doctorsFromCalendar = computed(() => store.state.calendar.doctors)
const doctorsWithChairs = computed(() => store.state.calendar.doctors)
const chairs = computed(() => store.state.auth.branchChairs)
const timeStep = computed(() => store.state.calendar.timeStep)
const newVisitTime = computed(() => store.state.calendar.newVisitTime)
const phoneFromCall = computed(() => store.state.calendar.phoneFromCall)
const newEloquentObject = computed(() => store.state.datatable.newEloquentObject)
const rights = computed(() => store.state.auth.rights)
const branch = computed(() => store.state.auth.branch)
const recordTags = computed(() => store.state.auth.record_tags)
const clinicRecordTypes = computed(() => store.state.auth.record_types)
const records = computed(() => store.state.calendar.records)
const leadForRecord = computed(() => store.state.calendar.leadForRecord)
const clientSources = computed(() => store.state.auth.clientSources)
const polises = computed(() => store.state.treatment.polises)
const isMobile = computed(() => store.state.dom.isMobile)
const maxEnabled = computed(() => store.getters.maxEnabled)
const isDentalClinic = computed(() => store.getters.isDentalClinic)

const doctors = computed(() => doctorsFromCalendar.value.length ? doctorsFromCalendar.value : doctorsFromAuth.value)
const datetime = computed(() => date.value+' '+time.value+':00')
const doctor_id = computed(() => props.visit ? props.visit.doctor_id : null)
const chair_id = computed(() => props.visit ? props.visit.chair_id : null)
const controlSystemAllowsEdit = computed(() => {
  if(!props.hasControlSystemStatus) return true
  if(rights.value.includes('edit_history')) return true
  if(visitIsToday.value && rights.value.includes('records_during_day')) return true
  return false
})
const canBeEdited = computed(() => props.visit
    && Number(props.visit.is_paid) !== 1
    && controlSystemAllowsEdit.value)
const visitIsToday = computed(() => {
  if(!props.visit?.start) return false
  return moment(props.visit.start).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
})
const polisCanBeEdited = computed(() => {
  if(!canBeEdited.value) return false
  return !(props.visit?.actual_services || []).length
})
const patientCanBeEdited = computed(() => {
  if(isOnlineBooking.value) return canBeEdited.value
  if(props.visit?.id && props.visit.client_id) return false
  return canBeEdited.value
})
const currentRecordTags = computed(() => (props.visit && props.visit.tags) ? props.visit.tags.map(el => el.id) : [])
const dateIsInSchedule = computed(() => {
  if(!props.visit.start) return true
  if(!doctorAvailableTimes.value) return false
  let res = false
  let endTime = props.visit.end.substr(11, 5);
  for(const ind in doctorAvailableTimes.value) {
    let x = doctorAvailableTimes.value[ind]
    let end = findEndTime(x, ind)
    if(time.value >= x.start && endTime <= end) {
      return true
    }
  }
  return res
})
const editBlock = computed(() => props.visit?.id && props.visit?.record_type === 'block')
const isOnlineBooking = computed(() => props.visit?.record_type == 'online' || visitRecordType.value?.default_type === 'online')
const polisesOptions = computed(() => polises.value.filter(polis => !props.visit?.insurance_company_id || Number(polis.insurance_company_id) === Number(props.visit.insurance_company_id)))
const visitRecordType = computed(() => clinicRecordTypes.value.find(rt => rt.id === props.visit?.record_type_id) || null)
const recordTypes = computed(() => clinicRecordTypes.value.filter(rt => rt.default_type !== 'online' || isOnlineBooking.value))
const conflictingRecordDoctor = computed(() => {
  if(!conflictingRecord.value || !conflictingRecord.value.doctor_id) return null
  return doctorsFromCalendar.value.find(x => x.id == conflictingRecord.value.doctor_id)
})

const calcEnd = () => {
  props.visit.end = moment(datetime.value).add(duration.value, 'minutes').format('YYYY-MM-DD HH:mm:ss')
}
const findEndTime = (t, ind) => {
  let end = t.time.substr(8,5)
  ind = parseInt(ind)
  const nextSlot = doctorAvailableTimes.value[((ind) + 1)] ?? null
  if(!nextSlot || nextSlot.start != end) return end
  return findEndTime(nextSlot, (ind + 1))
}
const reloadAvailableTimes = () => {
  if(doctor_id.value) {
    DoctorService.doctorsAvailabilityForDay(doctor_id.value, branch.value, date.value).then(res => {
      doctorAvailableTimes.value = res.data
    })
  } else {
    doctorAvailableTimes.value = null
  }
}
const addPatient = () => {
  store.commit('setViewModel', 'patient')
  store.commit('setModule', 'patients')
  let payload = {
    branch_id: branch.value,
    mphone: phoneFromCall.value ? phoneFromCall.value : null,
    send_marketing: true,
    send_notifications: true,
    document_type_id: 1
  }
  if(rights.value.includes('bonuses')
      && store.getters.getClinicAdditional('bonuses_for_new_patients')
      && store.getters.getClinicAdditional('bonuses_enabled')) {
    payload.use_bonuses = true
  } else {
    payload.use_bonuses = false
  }
  if(leadForRecord.value) {
    payload.fname = leadForRecord.value.fname
    payload.lname = leadForRecord.value.lname
    payload.email = leadForRecord.value.email
    payload.mphone = leadForRecord.value.phone || payload.mphone
    payload.source = leadForRecord.value.source_id
  } else if(props.visit.lead) {
    payload.fname = props.visit.lead.fname
    payload.lname = props.visit.lead.lname
    payload.email = props.visit.lead.email
    payload.mphone = props.visit.lead.phone || payload.mphone
    payload.source = props.visit.lead.source_id || 1293 // online-booking
  }
  store.commit('setSelectedModel', payload)
  store.commit('setShowModal', true)
}
const dateChanged = (val) => {
  date.value = val
  reloadAvailableTimes()
}
const timeSlotSelected = (timeObject) => {
  time.value = timeObject.start
  // duration.value = parseInt(timeObject.duration)
  timecounter.value++
}
const durationChange = (sign = 'plus') => {
  if(!canBeEdited.value) return
  duration.value = parseInt(duration.value)
  if(sign === 'plus') duration.value += parseInt(timeStep.value)
  if(sign === 'minus' && duration.value > timeStep.value) duration.value -= parseInt(timeStep.value)
}
const getConflictingRecord = () => {
  if(!datetime.value) return null
  if(!chair_id.value) return null
  conflictingRecord.value = records.value.find(x => {
    return x.id != props.visit.id && x.chair_id == chair_id.value &&
        (moment(datetime.value).isBetween(x.startMoment, x.endMoment, null, '[)') ||
            moment(props.visit.end).isBetween(x.startMoment, x.endMoment, null, '(]'))
  })
}
const toggleTag = (tag, action = 'add', index = null) => {
  if(action === 'add') {
    props.visit.tags.push(tag)
  }
  if(action === 'remove' && index !== null) {
    props.visit.tags.splice(index, 1)
  }
}
const openPatientDropdown = () => {
  patientSelect.value.openDropdown()
}
const findDefaultFieldId = async () => {
  if(!doctor_id.value) return
  if(props.visit.fields?.id) return
  const { data: { fields } } = await EloquentService.show('doctor', doctor_id.value)
  if(fields?.length) {
    props.visit.fields = fields[0]
  }
}
const addPolis = () => {
  store.commit('setViewModel', 'insurance_polis')
  store.commit('setModule', 'insurance')
  store.commit('setSelectedModel', {
    patient: props.visit?.patient || null,
    patient_id: props.visit?.patient?.id || null
  })
  store.commit('setShowModal', true)
}

defineExpose({ openPatientDropdown })

onMounted(() => {
  if(props.visit.id) {
    date.value = props.visit.start.substr(0,10)
    time.value = props.visit.start.substr(11,5)
    duration.value = parseInt(props.visit.durationMinutes)
    patient.value = props.visit.patient
    recordTypes.value = recordTypes.value.filter(x => x.id != 'block')
    if(props.visit.insurance_company_id) visitPayType.value = 'insurance'
  } else {
    date.value = newVisitTime.value.substr(0,10)
    time.value = newVisitTime.value.substr(11,5)
    duration.value = parseInt(props.visit.durationMinutes ? props.visit.durationMinutes : timeStep.value)
    if(props.visit.patient) {
      patient.value = props.visit.patient
    }
    props.visit.tags = []
  }
  if(props.visit.doctor_id) {
    reloadAvailableTimes()
    let d = doctorsWithChairs.value.find(x => x.id == props.visit.doctor_id)
    if(d?.chairs?.length && !props.visit.chair_id) {
      let branchDoctorChairs = d.chairs.filter(c => c.branch_id == branch.value)
      if(branchDoctorChairs.length) props.visit.chair_id = branchDoctorChairs[0].id
    }
    if(d) doctor.value = d
    findDefaultFieldId()
  }
  if(props.visit.chair_id) {
    chair.value = chairs.value.find(c => c.id == props.visit.chair_id)
  }
  if(patient.value?.user_additional?.max_messenger_used) {
    max_messenger_used.value = true
  }
  loaded.value = true
  setTimeout(() => {
    dateWatcher.value = true
  }, 100)
})

watch(datetime, (val) => {
  props.visit.start = val
  calcEnd()
  getConflictingRecord()
})
watch(max_messenger_used, (val) => {
  props.visit.max_messenger_used = val
})
watch(date, () => {
  if(dateWatcher.value) emit('timeChanged')
})
watch(time, () => {
  if(dateWatcher.value) emit('timeChanged')
})
watch(duration, () => {
  calcEnd()
  getConflictingRecord()
})
watch(patient, (val) => {
  let v = val?.id || null
  props.visit.client_id = v
  emit('changedPatient', v)
  if(val?.source && (!props.visit.source_id || props.visit.source_id === '-')) {
    props.visit.source_id = val.source
  }
  if(val) {
    EloquentService.dataTable('client_note', 1, {
      pagination: 999,
      condition: {
        client_id: val.id
      }
    }).then(res => {
      patientNotes.value = res?.data?.result?.data || []
    })
  }
  max_messenger_used.value = !!val?.user_additional?.max_messenger_used
})
watch(doctor, (val) => {
  props.visit.doctor_id = val ? val.id : null
})
watch(doctor_id, () => {
  reloadAvailableTimes()
  findDefaultFieldId()
})
watch(chair, (val) => {
  props.visit.chair_id = val ? val.id : null
})
watch(chair_id, () => {
  getConflictingRecord()
})
watch(newEloquentObject, (val) => {
  if(['patient', 'lead'].includes(val.model_name) && val.role_id && val.role_id === 5) { // also check for lead, cause modal can be opened from lead
    val.title = val.full_name
    patient.value = val
    patientSelectKey.value++
  }
})
watch(dateIsInSchedule, (val) => {
  emit('dateIsInSchedule', val)
})
watch(() => props.visit.record_type_id, (val) => {
  props.visit.record_type = clinicRecordTypes.value.find(x => Number(x.id) === Number(val))?.default_type || 'treatment'
})
watch(visitPayType, (val) => {
  props.visit.visitPayType = val
  if(val === 'payable') {
    props.visit.insurance_company_id = null
    props.visit.insurance_polis_id = null
  }
})
</script>

<style scoped lang="scss">
.visit-modal-col-divider {
  @media screen and (min-width: 992px) {
    border-right: 1px dashed $calendar-border-color;
  }
}
.visit-duration-control {
  //background: $gray-btn;
  border: 1px solid $gray-icon;
  border-radius: 8px;
  height: 40px;
  &.disabled {
    opacity: 0.6;
    pointer-events: none;
  }
  &-btn {
    flex: 0 0 40px;
    text-align: center;
    font-size: 20px;
    color: $blue-color;
    user-select: none;
    text-decoration: none;
    border-radius: 4px;
    margin-left: 5px;
    margin-right: 5px;
    &:hover {
      background-color: rgba($blue-color, 0.06);
    ;
    }
  }
  &-input {
    flex: 1 1 auto;
    min-width: 0;
    width: 0;
    border: none;
    background: transparent;
    text-decoration: underline;
    -moz-appearance: textfield;
    &::-webkit-outer-spin-button, &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
.visit-tag-chip {
  background: $gray-btn;
  border-radius: 8px;
  padding: 4px 10px;
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

@media screen and (max-width: 768px) {
  .visit-duration-control {
    height: 34px;
  }
  .visit-duration-control-btn {
    height: 26px;
    line-height: 26px;
    margin-left: 4px;
    margin-right: 4px;
  }
}
</style>
